/**
 * ProfileNavContext — Navbar-Kontext für offene Detail-Profile (T5).
 *
 * Statt einer eigenen Breadcrumb-Zeile in der Profilseite werden „Zurück zur
 * Liste" (links vom Seitentitel) und der Breadcrumb mit kopierbarer ID (rechts
 * vom Seitentitel) direkt in die globale Navbar (Header) gehoben. Dadurch
 * rutscht das Profil weiter nach oben und es ist mehr im Bild.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, getThemeColors } from '../../lib/theme';
import { useIsMobile } from '../../lib/useBreakpoint';

/* ─── NavSlots — Seiten heben Inhalte in die globale Navbar ────────────────
 *
 * Detailseiten (Familienkonto, BK-Profil) setzen ihre Kopfzeile („‹ Zurück",
 * Kontoname, „Konto bearbeiten") per useNavSlots() in die Navbar, statt eine
 * eigene Leiste zu rendern. App.tsx liest die Slots und reicht sie an den
 * Header durch. Beim Unmount der Seite werden die Slots automatisch geleert.
 */

interface NavSlots {
  leading: React.ReactNode;
  trailing: React.ReactNode;
  /**
   * Überschreibt den aktiven Menüpunkt (und Seitentitel), ohne die Route zu
   * ändern — z. B. /pflege/{id} eines Leads, der noch unter „Anfragen" läuft.
   * null = Menüpunkt aus der URL.
   */
  activeMenu: string | null;
}

interface NavSlotsContextValue extends NavSlots {
  setSlots: (slots: Partial<NavSlots>) => void;
  clearSlots: () => void;
}

const NavSlotsContext = createContext<NavSlotsContextValue | null>(null);

export function NavSlotsProvider({ children }: { children: React.ReactNode }) {
  const [slots, setSlotsState] = useState<NavSlots>({ leading: null, trailing: null, activeMenu: null });
  const value = useMemo<NavSlotsContextValue>(() => ({
    ...slots,
    setSlots: (s: Partial<NavSlots>) => setSlotsState(prev => ({ ...prev, ...s })),
    clearSlots: () => setSlotsState({ leading: null, trailing: null, activeMenu: null }),
  }), [slots]);
  return <NavSlotsContext.Provider value={value}>{children}</NavSlotsContext.Provider>;
}

/** Slots lesen (App.tsx → Header). */
export function useNavSlotsValue(): NavSlots {
  const ctx = useContext(NavSlotsContext);
  return ctx ?? { leading: null, trailing: null, activeMenu: null };
}

/**
 * Slots aus einer Detailseite setzen. Räumt beim Unmount automatisch auf.
 * `deps` steuert, wann die Slots neu gesetzt werden (z. B. [account?.name]).
 */
export function useNavSlots(slots: Partial<NavSlots>, deps: React.DependencyList) {
  const ctx = useContext(NavSlotsContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setSlots(slots);
    return () => ctx.clearSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** „‹ Zurück zur Liste"-Button — Header-leadingSlot. */
export function ProfileBackButton({ onBack, dark }: { onBack: () => void; dark: boolean }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  // Handy: nur das Chevron, sonst überlappt der Text die Navbar-Schalter.
  const iconOnly = useIsMobile();
  return (
    <button
      type="button"
      onClick={onBack}
      title={tr('pflege.detail.back')}
      aria-label={tr('pflege.detail.back')}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexShrink: 0,
        background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 8,
        padding: iconOnly ? 0 : '6px 12px', width: iconOnly ? 38 : undefined, height: iconOnly ? 38 : undefined,
        cursor: 'pointer', color: t.text,
        fontSize: 13, fontWeight: 500, fontFamily: 'Outfit,sans-serif', whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: iconOnly ? 20 : 16, lineHeight: 1 }}>‹</span>
      {!iconOnly && <> {tr('pflege.detail.back')}</>}
    </button>
  );
}

/**
 * Breadcrumb mit kopierbarer ID — Header-trailingSlot (T3 + T5).
 * Klick kopiert den Deep-Link `${origin}/pflege/${id}` in die Zwischenablage.
 */
export function ProfileBreadcrumb({ id, dark }: { id: string; dark: boolean }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = () => {
    const url = `${window.location.origin}/pflege/${id}`;
    try {
      navigator.clipboard?.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch { /* ignore */ }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, color: t.muted, fontSize: 13 }}>
      <span style={{ opacity: 0.6 }}>/</span>
      <button
        type="button"
        onClick={copy}
        title={copied ? tr('pflege.detail.copied') : tr('pflege.detail.copy_id')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
          color: copied ? '#039855' : BRAND, fontFamily: 'Outfit,sans-serif',
          fontSize: 13, fontWeight: 600,
        }}
      >
        #{id}
        <span aria-hidden style={{ fontSize: 12, opacity: 0.85 }}>{copied ? '✓' : '⧉'}</span>
      </button>
    </div>
  );
}
